import React, { useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { GridApi } from "ag-grid-community";
import { GridReadyEvent } from "ag-grid-community";

import {
  Dialog, DialogTitle, Typography, Grid, Tooltip, IconButton, Divider,
  DialogContent, Fab, ListItemIcon, ListItemText, MenuItem,
} from "@mui/material";

import AgGridService from "../../../../../../shared/services/ag-grid/ag-grid.service";
import useHelpers from "../../../../../../shared/services/helpers/useHelpers.hook";
import useReportingAndAnalyticsApi from "../../../useReportingAndAnalyticsApi";
import useAppStoreContext from "../../../../../../shared/context/app-store/app-store.context";
import useLoader from "../../../../../../shared/components/loading-dialog/useLoader.hook";
import IReportSelectedColumn from "../../../../../../shared/contracts/IReportSelectedColumn";
import LoadingDialogComponent from "../../../../../../shared/components/loading-dialog/loading-dialog.component";

import MuiIcon from "../../../../../../shared/styles/mui/helpers/mui-icon.helper";
import useReportDataFunctionsDialogService from "./report-data-functions-dialog.service";
import IColumnAndFunction from "../../../../../../shared/contracts/IColumnAndFunction";
import MenuButtonComponent from "../../../../../../shared/components/menu-button/menu-button.component";


export default function ReportDataFunctionsDialogComponent({
  open, onClose, onApply = () => { },
}: {
  open: boolean;
  onClose: () => void;
  onApply: () => void;
}) {
  const { hasDifference } = useHelpers();
  const { getDefaultColDef, getDefaultProps } = new AgGridService();
  const [gridApi, setGridApi] = useState<GridApi<IColumnAndFunction>>();

  const { getAvailableColumns, getSelectedColumns, saveSelectedColumns } = useReportingAndAnalyticsApi();
  const {
    isReportSelected, selReportDetails, selReportCode,
    defaultAvailableColumns, setDefaultAvailableColumns,
    defaultSelectedColumns, setDefaultSelectedColumns,
    currentSelectedColumns, setCurrentSelectedColumns,
  } = useAppStoreContext();

  const {
    clearGroupings, getColDefs, initializeRows,
    insertRow, getChanges, getSaveSelectedColumnsBody,
  } = useReportDataFunctionsDialogService();

  const [isPinned, setIsPinned] = useState<boolean>(false);
  const [reloadRequired, setReloadRequired] = useState<boolean>(false);

  useEffect(() => {
    if (defaultAvailableColumns.length <= 0) {
      availableColumnsLoader.loadAsync();
    }
    if (defaultSelectedColumns.length <= 0) {
      selectedColumnsLoader.loadAsync();
    }
  }, []);

  useEffect(() => {
    initializeRows(gridApi, currentSelectedColumns);
  }, [gridApi, currentSelectedColumns]);

  const handleGridReady = (e: GridReadyEvent<IColumnAndFunction>) => {
    setGridApi(e.api);
  };

  const availableColumnsLoader = useLoader({
    loaderName: "DataSource Columns",
    loadAsync: () =>
      getAvailableColumns(
        { rcd: selReportCode },
        {
          doIf: () =>
            (reloadRequired || defaultAvailableColumns.length <= 0) &&
            availableColumnsLoader.isNotLoading &&
            isReportSelected,
          doBefore: () => {
            availableColumnsLoader.SET_LOADING(
              "Fetching DataSource columns..."
            );
            setDefaultAvailableColumns([]);
          },
          onFulfilled: (data) => {
            setDefaultAvailableColumns(data.columns);
            availableColumnsLoader.SET_SUCCESS("Success.!");
          },
          doAfter: () => setReloadRequired(false),
          onException: availableColumnsLoader.SET_EXCEPTION,
        }
      ),
  });

  const selectedColumnsLoader = useLoader({
    loaderName: "Grid Columns",
    loadAsync: () =>
      getSelectedColumns({
        doIf: () =>
          (reloadRequired || defaultSelectedColumns.length <= 0) &&
          isReportSelected &&
          selectedColumnsLoader.isNotLoading,
        doBefore: () => {
          setDefaultSelectedColumns([]);
          selectedColumnsLoader.SET_LOADING("Fetching Grid Columns...");
        },
        onFulfilled: (data) => {
          setDefaultSelectedColumns(data);
          selectedColumnsLoader.SET_SUCCESS("Success.!");
        },
        doAfter: () => setReloadRequired(false),
        onException: selectedColumnsLoader.SET_EXCEPTION,
      }),
  });

  const saveSelectedColumnsLoader = useLoader({
    loaderName: "Save Selected Columns",
    loadAsync: () =>
      saveSelectedColumns(getSaveSelectedColumnsBody(getChanges(gridApi)), {
        doIf: () => saveSelectedColumnsLoader.isNotLoading,
        doBefore: () => {
          saveSelectedColumnsLoader.SET_LOADING("Saving Columns...");
        },
        onFulfilled: (data) => {
          saveSelectedColumnsLoader.SET_SUCCESS("Success.!");
          onApply();
        },
        onException: saveSelectedColumnsLoader.SET_EXCEPTION,
      }),
  });

  const handleReset = () => {
    initializeRows(gridApi, defaultSelectedColumns);
  };

  const handleApply = () => {
    const changes = getChanges(gridApi);
    setCurrentSelectedColumns([...changes]);
    onApply();
  };

  const handleSave = () => {
    const changes = getChanges(gridApi);
    setCurrentSelectedColumns([...changes]);
    setDefaultSelectedColumns([...changes]);
    saveSelectedColumnsLoader.loadAsync();
  };

  return (
    <>
      <LoadingDialogComponent loaderHook={availableColumnsLoader} />
      <LoadingDialogComponent loaderHook={selectedColumnsLoader} />
      <LoadingDialogComponent loaderHook={saveSelectedColumnsLoader} />

      {/* Filters Dialog */}
      <Dialog
        scroll="paper" maxWidth="lg" fullWidth
        fullScreen={isPinned} open={open} onClose={onClose}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            maxHeight: isPinned ? null : "664px",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Grid container direction="row" wrap="nowrap">
            <Grid item alignItems="start" container
              direction="row" wrap="nowrap"
            >
              <Grid item container direction="column" wrap="nowrap">
                <Grid item container direction="row"
                  justifyContent="start" alignItems="center"
                >
                  <Typography color="text.secondary"
                    variant="button" sx={{ textOverflow: "ellipsis" }}
                  >
                    Columns & Functions
                  </Typography>
                </Grid>

                <Typography flexGrow={1} variant="subtitle1">
                  {selReportDetails?.reportName}
                </Typography>
              </Grid>
            </Grid>

            <Grid item container direction="row" alignContent="start" justifyContent="center">
              <Tooltip placement="bottom" title={`${isPinned ? "Collapse" : "Expand"} Dialog`}>
                <IconButton color="primary" size="small" onClick={() => setIsPinned(!isPinned)}>
                  {isPinned ? MuiIcon.FOLD({ sx: {} }) : MuiIcon.UN_FOLD({ sx: {} })}
                </IconButton>
              </Tooltip>
            </Grid>

            <Grid
              item
              container
              direction="row"
              gap={1}
              justifyContent="end"
              wrap="nowrap"
              alignItems="center"
            >
              <MenuButtonComponent
                color="error"
                menuButtonColor="secondary"
                text="Cancel"
                onClick={onClose}
                size="small"
                variant="text"
                buttonVariant="contained"
                buttonIcon={MuiIcon.CANCEL()}
                menuButtonVariant="outlined"
                menuButtonIcon={MuiIcon.RELOAD()}
              >
                <MenuItem onClick={handleReset}>
                  <ListItemIcon>
                    {MuiIcon.RELOAD({
                      sx: { color: (t) => t.palette.error.main },
                    })}
                  </ListItemIcon>
                  <ListItemText sx={{}}>Reset Changes</ListItemText>
                </MenuItem>

                <MenuItem onClick={() => clearGroupings(gridApi)}>
                  <ListItemIcon>
                    {MuiIcon.CLEAR_ALL({
                      sx: { color: (t) => t.palette.error.main },
                    })}
                  </ListItemIcon>
                  <ListItemText sx={{}}>Clear Groups</ListItemText>
                </MenuItem>
              </MenuButtonComponent>

              <MenuButtonComponent
                color="primary"
                menuButtonColor="success"
                text="Apply"
                onClick={handleApply}
                size="small"
                variant="text"
                buttonVariant="contained"
                buttonIcon={MuiIcon.APPLY()}
                menuButtonVariant="outlined"
                menuButtonIcon={MuiIcon.SAVE()}
              >
                <MenuItem onClick={() => handleSave()}>
                  <ListItemIcon color="success">
                    {MuiIcon.SAVE({
                      sx: { color: (t) => t.palette.success.dark },
                    })}
                  </ListItemIcon>
                  <ListItemText sx={{ color: (t) => t.palette.success.dark }}>
                    Save Columns & Functions
                  </ListItemText>
                </MenuItem>
              </MenuButtonComponent>
            </Grid>
          </Grid>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ p: 0, minHeight: "512px" }}>
          {/* The AG Grid component */}
          <div className={"ag-theme-quartz"} style={{ height: "512px" }}>
            <AgGridReact<IColumnAndFunction>
              rowData={[]}
              columnDefs={getColDefs}
              defaultColDef={getDefaultColDef()}
              onGridReady={handleGridReady}
              {...getDefaultProps({
                rowHeight: 44,
                hideSideBar: true,
                rowDragManaged: true,
                autoSizeStrategy: { type: "fitGridWidth" },
              })}
            />
          </div>

          {/* Add Button */}
          <Tooltip title="Add New Column" placement="top-end">
            <Fab
              color="info"
              onClick={() => insertRow(gridApi)}
              sx={{ position: "absolute", bottom: 24, right: 24 }}
            >
              {MuiIcon.ADD()}
            </Fab>
          </Tooltip>
        </DialogContent>
      </Dialog>
    </>
  );
}